import React, { FC, useEffect, useRef } from 'react';
import { checkRoles, CLAIMS } from '../../utils/roles-helper';
import { FilterComponent } from '../FilterComponent';
import { SelectPicker } from 'rsuite';
import clsx from 'clsx';
import { IAuthState } from '../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { makeSearchParams, parseSearchParams } from '../../utils/utilities';
import { WhisperComponent } from '../developers/page-components/WhisperComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';

export interface ISettlementsFilters {
  batchType?: string;
  partnerId?: string;
  pageSize?: number;
  pageNumber?: number;
  mid?: string;
}

interface Props {
  batchType: string;
  partnerId: string;
  setBatchType: (value: string) => void;
  setPartnerId: (value: string) => void;
}

export const SettlementsFilterSection: FC<Props> = (props) => {
  const { batchType, setBatchType, partnerId, setPartnerId } = props;
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const isMounted = useRef(false);

  const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);

  const filtersFromUrl = parseSearchParams(search) || {};

  const { merchantState: { merchantsLists, whitelabelPartnersListSelector, chosenMerchant }} = useMerchants();

  useEffect(() => {
    if (!isMounted.current) return;

    const params: ISettlementsFilters = filtersFromUrl;
    params.batchType = batchType;
    params.partnerId = partnerId;
    params.mid = chosenMerchant;

    navigate(`${pathname}?${makeSearchParams(params)}`);
  }, [batchType, partnerId, chosenMerchant]);

  useEffect(() => {
    if (!isMounted.current) {
      // first mount
      isMounted.current = true;
    }
  }, []);

  return (
    <>
      {
        checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN) && (
          <div className="d-flex align-items-center fc-filter-line">
            <FilterComponent value={batchType} label={t('PENDING_PAYOUTS.BATCH_TYPES')} labelPlacement="integrated">
              <SelectPicker
                data={merchantsLists?.batchTypes_v2}
                appearance="subtle"
                placement="bottomStart"
                placeholder={t('PENDING_PAYOUTS.BATCH_TYPES')}
                onSelect={(type): void => setBatchType(type)}
                className={clsx('fc-selector timezone-selector', batchType ? 'fc-selector-cleanable' : 'fc-added-icon')}
                onClean={(): void => setBatchType('')}
                value={batchType}
                searchable={false}
                renderMenuItem={(label, item): JSX.Element => {
                  return (
                    <div className="d-flex align-items-center">
                      <div>{label}</div>
                      <WhisperComponent
                        isTooltip
                        whisperText={item?.description}
                        mainText={<FontAwesomeIcon icon={faQuestionCircle} width={12} />}
                        additionalClass="ms-1 cursor-pointer"
                      />
                    </div>
                  );
                }}
              />
            </FilterComponent>

            {
              checkRoles(authUser, CLAIMS.SUPER_ADMIN) && (
                <FilterComponent value={partnerId} label={t('PENDING_PAYOUTS.BATCH_SENDER')} labelPlacement="integrated">
                  <SelectPicker
                    data={whitelabelPartnersListSelector}
                    appearance="subtle"
                    placement="bottomStart"
                    placeholder={t('PENDING_PAYOUTS.BATCH_SENDER')}
                    onSelect={(pid): void => setPartnerId(pid)}
                    className={clsx('fc-selector timezone-selector', partnerId ? 'fc-selector-cleanable' : 'fc-added-icon')}
                    onClean={(): void => setPartnerId('')}
                    value={partnerId}
                  />
                </FilterComponent>
              )
            }
          </div>
        )}
    </>
  );
};